import { useCallback } from "react";
import { useCrossmint, useWallet } from "@crossmint/client-sdk-react-ui";
import { CrossmintWallets } from "@crossmint/wallets-sdk";
import { CROSSMINT_ENVIRONMENT } from "./crossmintDepositConfig";
import { getSwapSigner } from "./getSwapSigner";

// The React useWallet hook caches one wallet and returns it for later calls,
// even when a different chain is requested. Use the SDK factory per chain.
export const useCrossmintChainWallet = () => {
  const { crossmint } = useCrossmint();
  const { onAuthRequired } = useWallet();

  return useCallback(async (chain) => {
    const wallets = CrossmintWallets.from(crossmint);
    const args = {
      chain,
      // The direct factory is needed for each chain, but unlike useWallet it
      // does not add the provider's OTP callback automatically. Without it,
      // sending a transaction fails before anything reaches the network.
      signer: { type: "email", onAuthRequired },
    };
    const signer = CROSSMINT_ENVIRONMENT === "production" ? await getSwapSigner(chain) : null;
    let wallet;
    if (signer) {
      try {
        // A new wallet receives backend signing authority at creation.
        wallet = await wallets.getOrCreateWallet({ ...args, delegatedSigners: [{ signer: signer.locator }] });
      } catch {
        // Existing wallets predate the server signer and need one OTP-backed
        // enrollment. If initial registration is unavailable, preserve wallet
        // access and offer enrollment in the swap flow.
      }
    }
    if (!wallet) wallet = await wallets.getOrCreateWallet(args);
    if (!wallet?.address || wallet.chain !== chain) {
      throw new Error(`Wallet unavailable for ${chain}`);
    }
    return wallet;
  }, [crossmint, onAuthRequired]);
};
